import { useLocation, useNavigate } from 'react-router-dom';
import { Button, Dialog, DialogContent, Stack, Typography } from '@mui/material';
import { Favorite } from '@mui/icons-material';
import { rememberPendingSave } from './pendingSave';
import { brand, text } from '../../theme/colors';

const INK = '#1e293b';

/**
 * A signed-out heart used to jump straight to the generic "Select role" sign-up
 * page, which never said anything about saving — to a buyer it looked like the
 * button was broken (owner, 2026-09-26). Now it asks, in place: log in, or
 * make a free buyer account. Either way the home is remembered and saved the
 * moment they land back on this page.
 *
 * `listingId` is the home being saved (null closes the dialog).
 */
export default function SignInToSaveDialog({ listingId, onClose }) {
  const navigate = useNavigate();
  const location = useLocation();
  const next = `${location.pathname}${location.search}`;

  const go = (path) => {
    rememberPendingSave(listingId);
    navigate(path, { state: { from: location, saveListingId: listingId } });
  };

  return (
    <Dialog open={!!listingId} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogContent sx={{ p: 3.5 }}>
        <Stack spacing={2} alignItems="flex-start">
          <Favorite sx={{ color: '#c2410c', fontSize: 34 }} />
          <Typography component="h2" sx={{ fontSize: 24, fontWeight: 800, color: INK, lineHeight: 1.2 }}>Save this home</Typography>
          <Typography sx={{ color: text.secondary, lineHeight: 1.55 }}>
            Keep the homes you like in one place and come back to them from any device. It takes a free Eqaya account.
          </Typography>
          <Stack spacing={1.25} sx={{ width: '100%', pt: 1 }}>
            <Button fullWidth onClick={() => go(`/register?role=client&next=${encodeURIComponent(next)}`)}
              sx={{ height: 50, borderRadius: '10px', bgcolor: brand.primaryDark, color: '#fff', textTransform: 'none', fontWeight: 700, fontSize: 16, '&:hover': { bgcolor: '#134e4a' } }}>
              Create a free account
            </Button>
            <Button fullWidth onClick={() => go(`/login?next=${encodeURIComponent(next)}`)}
              sx={{ height: 50, borderRadius: '10px', border: `1.5px solid ${INK}`, color: INK, textTransform: 'none', fontWeight: 700, fontSize: 16 }}>
              I already have an account — log in
            </Button>
            <Button fullWidth onClick={onClose} sx={{ textTransform: 'none', color: text.secondary }}>Not now</Button>
          </Stack>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
